import React, {useState, useEffect, useRef} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./FileUpload.css";
import {Button, Icon, IconButton} from "@spg/component_library";
import closeIcon from "../../resources/images/closeIcon.svg";
import loadingSpinner from "../../resources/images/loadingSpinner.svg";
import cloudUpload from "../../resources/images/cloudUpload.svg";
import API from "../../logic/API";
import {ErrorReason} from "../../logic/APITypes";
import {keycloak} from "../../utility/KeycloakProvider";

type UploadFile = {
  file: File;
  id: string;
  progress: number;
  message?: string;
  status: "queued" | "staged" | "uploading" | "failed" | "prohibited";
};

type FileUploadProps = {
  startDate: Date | undefined;
  endDate: Date | undefined;
  note: string;
  /** Function to call after successfully sending the request */
  onSuccess: () => void;
  /** Function to set the state for whether the request is in progress */
  setWaitingForResponse: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether to prevent the users from selecting files (or using any buttons) – the element should explain why */
  isDisabled?: JSX.Element;
  /** List of files to initialize the component with for testing */
  unitTestFiles?: UploadFile[];
};
const maxFileSize = 500 * 1000 * 1000;

/**
 * A component allowing users to upload one or multiple files.
 */
function FileUpload(props: FileUploadProps) {
  const intl = useIntl();

  const [draggingFileOver, setDraggingFileOver] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>(
    process.env.NODE_ENV === "test" && props.unitTestFiles
      ? props.unitTestFiles
      : [],
  );
  const currentUpload = useRef<
    {fileId: string} & ReturnType<typeof API.createDisplayBookingRequest>
  >();
  const tokenAbortController = useRef<AbortController>(new AbortController());
  const [dummyInputValue, setDummyInputValue] = useState(""); //allows onInput to be triggered repeatedly even if the same file selection is made
  const [accessToken, setAccessToken] = useState("");
  const [retryAllowed, setRetryAllowed] = useState(true);

  const uploadInputValid = uploadFiles.length > 0;
  uploadFiles.some((file) => file.status != "prohibited");

  const uploadButtonAsRetry =
    uploadInputValid &&
    uploadFiles.every(
      (file) => file.status == "failed" || file.status == "prohibited",
    );
  const cancelButtonVisible =
    uploadInputValid &&
    uploadFiles.some(
      (file) => file.status == "staged" || file.status == "uploading",
    );

  const labelText = cancelButtonVisible
    ? "single.uploading"
    : draggingFileOver
      ? "single.release"
      : uploadFiles.length == 0
        ? "single.default"
        : uploadFiles[0].status == "failed"
          ? "single.failed"
          : "single.replace";

  /** Checks for staged files and calls the upload-Endpoint with callbacks for what to do afterwards. */
  const initializeUpload = () => {
    setUploadFiles((ufOuter) => {
      const nextFileIndex = ufOuter.findIndex(
        (file) => file.status === "staged",
      );
      if (nextFileIndex == -1) return ufOuter;
      if (!accessToken) {
        tokenAbortController.current.abort();
        tokenAbortController.current = new AbortController();
        API.getDisplayBookingAccessToken().then((response) => {
          if (response.success && response.payload) {
            setAccessToken(response.payload.token);
          } else {
            setUploadFiles((uf) =>
              uf.map((file) => {
                if (["uploading", "staged", "queued"].includes(file.status)) {
                  return {
                    ...file,
                    status: "failed",
                    message: intl.formatMessage({
                      id: "fileUpload.list.error.token",
                    }),
                  };
                }
                return file;
              }),
            );
            currentUpload.current = undefined;
          }
        });
        return ufOuter;
      }
      const newFiles = [...ufOuter];
      const nextFile = newFiles[nextFileIndex];
      newFiles[nextFileIndex] = {
        ...nextFile,
        status: "uploading",
        message: undefined,
      };
      if (!nextFile) return newFiles;
      if (currentUpload.current) {
        currentUpload.current.reader.abort();
        currentUpload.current.xhr.abort();
      }
      const onProgress = (e: ProgressEvent) => {
        setUploadFiles((uf) =>
          uf.map((file) =>
            file.id === nextFile.id
              ? {
                  ...file,
                  progress: Math.round((10000 * e.loaded) / e.total) / 100,
                }
              : file,
          ),
        );
      };
      const onLoad = () => {
        props.setWaitingForResponse(false);
        if (!currentUpload.current) return;
        const updatedStatus =
          200 <= currentUpload.current.xhr.status &&
          currentUpload.current.xhr.status < 300
            ? "finished"
            : "failed";

        if (updatedStatus === "finished") {
          setUploadFiles([]);
          props.onSuccess();
        } else {
          let error: ErrorReason & {actual?: string; expected?: string} = {
            msg: "generic",
          };
          try {
            error = JSON.parse(currentUpload.current.xhr.responseText)[
              "detail"
            ][0];
            if (
              ![
                "FILE_UNSUPPORTED_TYPE",
                "IMAGE_FORMAT_NOT_IMPLEMENTED",
                "IMAGE_WRONG_COLOR_MODE",
                "IMAGE_WRONG_RESOLUTION",
                "IMAGE_INVALID",
                "VIDEO_INVALID",
                "VIDEO_WRONG_RESOLUTION",
                "VIDEO_WRONG_FPS",
                "VIDEO_WRONG_DURATION",
                "VIDEO_BITRATE_TOO_LOW",
                "VIDEO_HAS_B_FRAMES",
              ].includes(error.msg)
            )
              error.msg = "generic";
            else setRetryAllowed(false);
          } finally {
            setUploadFiles((uf) =>
              uf.map((file) => ({
                ...file,
                status: "failed",
                progress: 0,
                message: intl.formatMessage(
                  {
                    id: "fileUpload.list.error." + error.msg,
                  },
                  {actual: error.actual, expected: error.expected},
                ),
              })),
            );
          }
        }
        currentUpload.current = undefined;
      };
      const onFail = () => {
        props.setWaitingForResponse(false);
        setUploadFiles((uf) =>
          uf.map((file) => ({
            ...file,
            status: "failed",
            message: intl.formatMessage({
              id: "fileUpload.list.error.generic",
            }),
          })),
        );
        currentUpload.current = undefined;
      };
      props.setWaitingForResponse(true);
      if (props.startDate && props.endDate) {
        const normalizedStart = new Date(props.startDate.valueOf());
        normalizedStart.setMinutes(-1 * props.startDate.getTimezoneOffset());
        const normalizedEnd = new Date(props.endDate.valueOf());
        normalizedEnd.setMinutes(-1 * props.endDate.getTimezoneOffset());
        currentUpload.current = {
          fileId: nextFile.id,
          ...API.createDisplayBookingRequest(
            normalizedStart,
            normalizedEnd,
            props.note,
            nextFile.file,
            onProgress,
            onLoad,
            onFail,
            keycloak.token ?? "",
            accessToken,
          ),
        };
      }
      return newFiles;
    });
  };

  useEffect(initializeUpload, [uploadFiles, accessToken]);

  useEffect(
    () => () => {
      if (currentUpload.current) {
        currentUpload.current.reader.abort();
        currentUpload.current.xhr.abort();
      }
    },
    [],
  );

  return (
    <div className="page__grid fileUploadContainer">
      {/* File selection input and status indicator */}
      <div
        className={`fileUpload${draggingFileOver ? " draggingFileOver" : ""}${
          props.isDisabled ? " disabled" : ""
        }`}
        onDrop={(e) => {
          if (cancelButtonVisible || props.isDisabled) return;
          setDraggingFileOver(false);
          e.preventDefault();
          let files: UploadFile[] = [];
          const time = Date.now();
          [...Array.from(e.dataTransfer.items)].forEach((item, i) => {
            const file = item.getAsFile();
            if (item.kind === "file" && file)
              files.push({
                file,
                id: `${time}-${i}`,
                progress: 0,
                status: file.size > maxFileSize ? "prohibited" : "queued",
              });
          });
          if (files.length == 0)
            files = Array.from(e.dataTransfer.files).map((file, i) => {
              return {
                file,
                id: `${time}-${i}`,
                progress: 0,
                status: file.size > maxFileSize ? "prohibited" : "queued",
              };
            });
          setRetryAllowed(true);
          setUploadFiles([files[0]]);
        }}
        onDragOver={(e) => {
          if (cancelButtonVisible || props.isDisabled) return;
          if (
            [...Array.from(e.dataTransfer.items)].some(
              (item) => item.kind === "file",
            ) ||
            e.dataTransfer.files.length > 0
          )
            setDraggingFileOver(true);
          e.preventDefault();
        }}
        onDragEnd={() => setDraggingFileOver(false)}
        onDragLeave={() => setDraggingFileOver(false)}
        onDragExit={() => setDraggingFileOver(false)}
      >
        <input
          type="file"
          id="fileUploadInput"
          className="sr-only"
          value={dummyInputValue}
          accept="image/jpeg,video/mp4"
          onInput={(e) => {
            setRetryAllowed(true);
            setDummyInputValue("");
            const time = Date.now();
            setUploadFiles(() => {
              const newFiles = Array.from(
                (e.target as EventTarget & HTMLInputElement).files || [],
              ).map((file, i) => ({
                file,
                id: `${time}-${i}`,
                progress: 0,
                status:
                  file.size > maxFileSize
                    ? "prohibited"
                    : ("queued" as UploadFile["status"]),
              }));
              return [newFiles[0]];
            });
          }}
          disabled={cancelButtonVisible || !!props.isDisabled}
        />
        <label htmlFor="fileUploadInput">
          <Icon
            href={cloudUpload + "#globalG"}
            title={""}
            iconSize={{width: 72, height: 72}}
            strokeColor={draggingFileOver ? "white" : "black"}
          />
          <span>
            <div>
              {props.isDisabled || (
                <FormattedMessage
                  id={"fileUpload.label." + labelText}
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                    br: () => <br />,
                  }}
                />
              )}
            </div>
          </span>
          {labelText == "single.default" && (
            <span className="fileUploadLabelInfo">
              <FormattedMessage id="fileUpload.label.info" />
            </span>
          )}
        </label>
      </div>
      {/* List of files to upload */}
      {uploadFiles.length > 0 && (
        <ul id="fileQueue" className="fileUploadFiles">
          {uploadFiles.map((file, i) => (
            <li key={file.id} className={file.status}>
              <span>{file.file.name}</span>
              {file.status == "prohibited" ? (
                <span className="fileUploadFilesTextContainer">
                  <FormattedMessage id="fileUpload.list.error.size" />
                </span>
              ) : (
                <span className="fileUploadFilesTextContainer">
                  {file.message}
                </span>
              )}
              <span className="fileUploadFilesButtonContainer">
                {(file.status == "queued" && cancelButtonVisible) ||
                file.status == "uploading" ||
                file.status == "staged" ? (
                  <img
                    src={loadingSpinner}
                    alt={intl.formatMessage({
                      id: "fileUpload.list.loading",
                    })}
                    width={24}
                    height={24}
                  />
                ) : (
                  <IconButton
                    idBase={"fileUploadFilesRemove" + file.id}
                    onClick={() => {
                      const newFiles = [...uploadFiles];
                      newFiles.splice(i, 1);
                      setUploadFiles(newFiles);
                    }}
                    disabled={!!props.isDisabled}
                    iconProps={{
                      href: closeIcon + "#globalG",
                      strokeColor: "black",
                      title: intl.formatMessage({
                        id: "fileUpload.list.remove",
                      }),
                    }}
                  />
                )}
              </span>
              {((file.status == "queued" && cancelButtonVisible) ||
                file.status == "staged" ||
                file.status == "uploading") && (
                <progress
                  max={100}
                  value={file.progress}
                  aria-label={intl.formatMessage(
                    {
                      id: "fileUpload.list.progress",
                    },
                    {fileName: file.file.name},
                  )}
                >
                  {file.progress + "%"}
                </progress>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="fileUploadButtons">
        {cancelButtonVisible && (
          <Button
            label={intl.formatMessage({id: "fileUpload.button.cancel"})}
            onClick={() => {
              if (currentUpload.current) {
                currentUpload.current.reader.abort();
                currentUpload.current.xhr.abort();
                currentUpload.current = undefined;
              }
              setUploadFiles((uf) =>
                uf.map((file) =>
                  file.status === "prohibited"
                    ? file
                    : {
                        ...file,
                        status: "failed",
                        progress: 0,
                        message: intl.formatMessage({
                          id: "fileUpload.list.cancel",
                        }),
                      },
                ),
              );
            }}
            disabled={!!props.isDisabled}
            configuration={"tertiary"}
            isWide
          />
        )}
        <Button
          label={
            uploadButtonAsRetry
              ? intl.formatMessage(
                  {
                    id: `fileUpload.button.single.retry`,
                  },
                  {
                    count: uploadFiles.filter((file) => file.status == "failed")
                      .length,
                  },
                )
              : intl.formatMessage({id: "fileUpload.button.upload"})
          }
          onClick={() => {
            setUploadFiles((uf) => {
              const newFiles: UploadFile[] = uploadButtonAsRetry
                ? uf.map((file) =>
                    file.status == "failed"
                      ? {...file, status: "queued"}
                      : file,
                  )
                : [...uf];
              const firstQueuedIndex = newFiles.findIndex(
                (file) => file.status === "queued",
              );
              newFiles[firstQueuedIndex] = {
                ...newFiles[firstQueuedIndex],
                status: "staged",
              };
              return newFiles;
            });
          }}
          configuration={"primary"}
          isWide
          disabled={
            !retryAllowed ||
            !uploadInputValid ||
            cancelButtonVisible ||
            !!props.isDisabled ||
            !props.startDate ||
            !props.endDate ||
            props.startDate > props.endDate
          }
        />
      </div>
    </div>
  );
}

export {FileUpload, type UploadFile};
